/**
 * "Needs review" — the cells on this grid the payload can flag.
 *
 * A worklist, not a second grid: the point is to walk it and clear it, so each
 * row is one click that takes the reader BACK to the Data tab with that cell's
 * evidence open. Nothing is edited here; the drill-in already owns overriding.
 *
 * It reads the scope that is on screen (see `needsReviewItems`), so switching
 * property or grain changes what it lists — which is the honest behaviour: an
 * unverified number belongs to a grid, not to the dashboard as a whole.
 *
 * The list is UNVERIFIED cells and nothing else. `held_reason` never reaches
 * `/api/kpi/table`, so a held value is indistinguishable from an empty one on
 * the wire and this tab cannot see it — which makes an empty list "nothing
 * unverified", not "everything checks out". Every string below says that much
 * and no more; the reassurance the payload cannot back is exactly the kind a
 * reader would act on.
 */

import React from "react";
import { ChevronRight } from "lucide-react";

import type { KpiReviewItem } from "./kpiNeedsReview";
import { formatPeriod } from "./formatKpiValue";

export interface KpiNeedsReviewTabProps {
	items: KpiReviewItem[];
	/** The grain the periods are IN, so the labels match the grid's columns. */
	grain: string;
	/** Where the scope being reviewed is named — "Portfolio", or a property. */
	scopeLabel: string;
	onOpenCell: (metricKey: string, period: string) => void;
}

/**
 * Said on both the empty and the populated state, because it qualifies the LIST
 * rather than the count: a reader who is told nothing is unverified will
 * otherwise take the tab for a clean bill of health on the whole grid.
 */
const HELD_CAVEAT = "Held values are not surfaced here yet.";

const KpiNeedsReviewTab: React.FC<KpiNeedsReviewTabProps> = ({
	items,
	grain,
	scopeLabel,
	onOpenCell,
}) => {
	if (items.length === 0) {
		return (
			<div className="space-y-1">
				<p className="text-sm text-text-muted">
					No unverified cells on {scopeLabel}.
				</p>
				<p className="text-xs text-text-muted">{HELD_CAVEAT}</p>
			</div>
		);
	}

	return (
		<div className="space-y-3">
			<div className="space-y-1">
				<p className="text-sm text-text-muted">
					{items.length} {items.length === 1 ? "value" : "values"} on{" "}
					{scopeLabel} were extracted but not verified against the document
					they came from. Open one to see its evidence.
				</p>
				<p className="text-xs text-text-muted">{HELD_CAVEAT}</p>
			</div>
			<ul className="divide-y divide-gray-100 overflow-hidden rounded-lg border border-gray-200 bg-white">
				{items.map((item) => (
					<li key={item.id}>
						<button
							type="button"
							onClick={() => onOpenCell(item.metricKey, item.period)}
							className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-gray-50"
						>
							<span className="min-w-0 flex-1">
								<span className="block truncate text-base font-medium text-text-base">
									{item.metricLabel}
								</span>
								<span className="block truncate text-xs text-text-muted">
									{item.groupLabel} · {item.reason}
								</span>
							</span>
							<span className="shrink-0 text-sm text-text-muted">
								{formatPeriod(item.period, grain)}
							</span>
							<ChevronRight
								className="h-4 w-4 shrink-0 text-text-muted"
								aria-hidden="true"
							/>
						</button>
					</li>
				))}
			</ul>
		</div>
	);
};

export default KpiNeedsReviewTab;
